import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { isLoggedIn } from "../auth/authStorage";
import { isCanceledMembership } from "../explorer/isUnpaidExplorerUser";
import { useCurrentMembership } from "./useCurrentMembership";

/**
 * Explorer users without full membership: show sidebar shell but lock nav behind membership.
 * Paid members use the normal sidebar behaviour.
 * `/register-user` stays full-width with no sidebar, except explorers upgrading to membership.
 */
export function useMembershipSidebarGate() {
  const location = useLocation();
  const authed = isLoggedIn();
  const { isExplorer, isMember, profile } = useCurrentMembership();

  const isCancelled = useMemo(
    () => isCanceledMembership(profile),
    [profile],
  );

  const isRegistrationWizard = location.pathname === "/register-user";

  /** Membership wizard is full-width; explorers keep their sidebar while upgrading. */
  const hideSidebarOnRegistrationWizard = isRegistrationWizard && !isExplorer;

  const lockSidebarForMembership = useMemo(
    () =>
      authed &&
      isExplorer &&
      !hideSidebarOnRegistrationWizard,
    [authed, isExplorer, hideSidebarOnRegistrationWizard],
  );

  const treatAsSidebarMember = hideSidebarOnRegistrationWizard
    ? false
    : isMember || isCancelled || lockSidebarForMembership;

  return {
    lockSidebarForMembership,
    treatAsSidebarMember,
    hideSidebarOnRegistrationWizard,
  };
}
